import { useEffect, useRef } from "react";

const NODES = [
  { x: 0.16, y: 0.28, label: "PEOPLE" },
  { x: 0.38, y: 0.16, label: "PROCESS" },
  { x: 0.62, y: 0.26, label: "TECHNOLOGY" },
  { x: 0.86, y: 0.18, label: "GOVERNANCE" },
  { x: 0.28, y: 0.52 },
  { x: 0.52, y: 0.48, label: "OPERATING REALITY", hub: true },
  { x: 0.76, y: 0.52 },
  { x: 0.2, y: 0.78, label: "DATA" },
  { x: 0.46, y: 0.8 },
  { x: 0.68, y: 0.78, label: "OUTCOMES" },
  { x: 0.9, y: 0.6 },
  { x: 0.08, y: 0.55 },
];

const EDGES = [
  [0, 1], [1, 2], [2, 3], [0, 4], [4, 5], [1, 5], [2, 6], [5, 6],
  [4, 7], [5, 8], [6, 9], [7, 8], [8, 9], [6, 10], [3, 10], [0, 11],
  [11, 7], [5, 9], [2, 5],
];

export default function HeroDiagram({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);

    const onMove = (e) => {
      mouse.tx = e.clientX / window.innerWidth;
      mouse.ty = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", onMove);

    const nodePos = (n, i, t) => {
      const drift = reduced ? 0 : 1;
      const px = (mouse.x - 0.5) * 26 * drift;
      const py = (mouse.y - 0.5) * 18 * drift;
      return {
        x: n.x * w + Math.sin(t * 0.00035 + i * 1.7) * 5 * drift + px,
        y: n.y * h + Math.cos(t * 0.0003 + i * 2.3) * 4 * drift + py,
      };
    };

    const draw = (t) => {
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;
      ctx.clearRect(0, 0, w, h);
      const pts = NODES.map((n, i) => nodePos(n, i, t));

      EDGES.forEach(([a, b]) => {
        ctx.beginPath();
        ctx.moveTo(pts[a].x, pts[a].y);
        ctx.lineTo(pts[b].x, pts[b].y);
        ctx.strokeStyle = "rgba(56, 189, 248, 0.13)";
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      if (!reduced) {
        EDGES.forEach(([a, b], i) => {
          const p = (t * 0.000045 * (1 + (i % 4) * 0.35) + i * 0.13) % 1;
          const x = pts[a].x + (pts[b].x - pts[a].x) * p;
          const y = pts[a].y + (pts[b].y - pts[a].y) * p;
          ctx.beginPath();
          ctx.arc(x, y, 1.6, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(56, 189, 248, 0.85)";
          ctx.shadowColor = "rgba(56, 189, 248, 0.9)";
          ctx.shadowBlur = 8;
          ctx.fill();
          ctx.shadowBlur = 0;
        });
      }

      NODES.forEach((n, i) => {
        const p = pts[i];
        if (n.hub) {
          const pulse = reduced ? 0 : Math.sin(t * 0.0012) * 3;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 30 + pulse, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(56, 189, 248, 0.55)";
          ctx.lineWidth = 1.2;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(p.x, p.y, 38 + pulse * 1.4, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(56, 189, 248, 0.14)";
          ctx.setLineDash([3, 5]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, 3.4, 0, Math.PI * 2);
          ctx.fillStyle = "#38BDF8";
          ctx.fill();
          ctx.font = "500 8.5px 'IBM Plex Mono', monospace";
          ctx.fillStyle = "rgba(226, 232, 240, 0.85)";
          ctx.textAlign = "center";
          ctx.fillText("OPERATING", p.x, p.y - 7);
          ctx.fillText("REALITY", p.x, p.y + 13);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, n.label ? 2.6 : 1.8, 0, Math.PI * 2);
          ctx.fillStyle = n.label ? "rgba(148, 163, 184, 0.9)" : "rgba(100, 116, 139, 0.6)";
          ctx.fill();
        }
        if (n.label && !n.hub) {
          ctx.font = "500 9px 'IBM Plex Mono', monospace";
          ctx.fillStyle = "rgba(148, 163, 184, 0.7)";
          ctx.textAlign = "center";
          ctx.fillText(n.label, p.x, p.y - 9);
        }
      });

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    if (reduced) draw(0);
    else raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <div className={`absolute inset-0 ${className}`} data-testid="hero-dependency-diagram" aria-hidden="true">
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}
